import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuth } from '../App';
import Header from '../components/Header';
import {
  ShieldCheck,
  Globe,
  ArrowLeftRight,
  ArrowUpRight,
  Camera,
  X,
  FileText,
  Code2,
  Search,
} from 'lucide-react';

// Single-purpose, locally-scoped CSS so the rest of the app is unaffected.
// We keep the original Tagora light/cream palette and just add the grid
// background + button glow + entrance animations.
const PageStyles = () => (
  <style>{`
    .tg-grid-bg {
      background-image:
        linear-gradient(rgba(18,18,18,0.045) 1px, transparent 1px),
        linear-gradient(90deg, rgba(18,18,18,0.045) 1px, transparent 1px);
      background-size: 56px 56px;
    }
    .tg-hero-glow {
      position: absolute; inset: 0; pointer-events: none;
      background: radial-gradient(60% 50% at 50% 0%, rgba(255,138,101,0.18), transparent 70%);
    }
    .tg-btn-glow:hover {
      box-shadow: 0 0 0 6px rgba(255,138,101,0.12), 0 12px 28px -10px rgba(255,138,101,0.55);
    }
    @keyframes tg-fade-up {
      from { opacity: 0; transform: translateY(14px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .tg-anim { animation: tg-fade-up 700ms cubic-bezier(0.4,0,0.2,1) both; }
    .tg-anim-d1 { animation-delay: 90ms; }
    .tg-anim-d2 { animation-delay: 180ms; }
    .tg-anim-d3 { animation-delay: 270ms; }
    .tg-anim-d4 { animation-delay: 360ms; }
    @media (prefers-reduced-motion: reduce) {
      .tg-anim, .tg-anim-d1, .tg-anim-d2, .tg-anim-d3, .tg-anim-d4 { animation: none; }
    }
    /* Hide html5-qrcode default chrome */
    #tg-qr-reader { width: 100% !important; }
    #tg-qr-reader video { width: 100% !important; height: auto !important; border-radius: 12px; }
    #tg-qr-reader__scan_region img { display: none !important; }
    #tg-qr-reader__dashboard_section_csr,
    #tg-qr-reader__dashboard_section_swaplink,
    #tg-qr-reader__dashboard_section_fsr,
    #tg-qr-reader__header_message,
    #tg-qr-reader__filescan_input,
    #tg-qr-reader__status_span { display: none !important; }
  `}</style>
);

// ---------- Hero ----------
const HeroBlock = ({ onScanClick }) => (
  <section className="relative overflow-hidden" data-testid="tg-hero">
    <div className="absolute inset-0 tg-grid-bg" aria-hidden="true" />
    <div className="tg-hero-glow" aria-hidden="true" />
    <div className="relative max-w-5xl mx-auto px-6 md:px-8 pt-16 md:pt-24 pb-20 md:pb-28 text-center">
      <p className="tg-anim text-xs tracking-[0.25em] uppercase text-charcoal/60 mb-6" data-testid="tg-hero-kicker">
        Infrastructure de propriété certifiée
      </p>
      <h1 className="tg-anim tg-anim-d1 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal tracking-tight" data-testid="tg-hero-title">
        Votre bien a une histoire.<br />
        <span className="text-primary">Tagora la certifie.</span>
      </h1>
      <p className="tg-anim tg-anim-d2 mt-6 text-base md:text-lg text-charcoal/70 max-w-2xl mx-auto leading-relaxed" data-testid="tg-hero-sub">
        Chaque produit certifié Tagora porte un QR code unique. Scannez-le pour vérifier son authenticité,
        contacter son propriétaire, ou transmettre la propriété en toute sécurité — sans jamais révéler votre identité.
      </p>
      <p className="tg-anim tg-anim-d2 mt-4 text-sm md:text-base text-charcoal/80 italic font-medium" data-testid="tg-hero-tagline">
        Un lien permanent entre vous et ce que vous possédez.
      </p>
      <div className="tg-anim tg-anim-d3 mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 flex-wrap">
        <button onClick={onScanClick} className="btn-primary tg-btn-glow inline-flex items-center justify-center gap-2" data-testid="tg-cta-scan">
          <Camera className="w-4 h-4" /> Scanner un QR Tagora
        </button>
        <Link to="/verify" className="btn-outline inline-flex items-center justify-center gap-2" data-testid="tg-cta-verify">
          <Search className="w-4 h-4" /> Vérifier un code
        </Link>
        <Link to="/business#contact" className="btn-outline inline-flex items-center justify-center gap-2" data-testid="tg-cta-integrate">
          Intégrer Tagora à votre marque <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="tg-anim tg-anim-d4 mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-charcoal/60" data-testid="tg-hero-badges">
        <span className="inline-flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-primary" /> Anonymat de bout en bout</span>
        <span className="inline-flex items-center gap-2"><Globe className="w-3.5 h-3.5 text-primary" /> Compatible DPP EU 2026</span>
        <span className="inline-flex items-center gap-2"><ArrowLeftRight className="w-3.5 h-3.5 text-primary" /> Transfert en 1 clic</span>
      </div>
    </div>
  </section>
);

// ---------- Scanner ----------
const ScannerBlock = ({ scannerHostRef, scanState, error, demoResult, onStart, onStop, onReset }) => (
  <section className="relative" id="tg-scanner" data-testid="tg-scanner-section">
    <div className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24">
      <div className="text-center mb-10">
        <p className="text-xs tracking-[0.25em] uppercase text-charcoal/60 mb-3">02 — Vérification</p>
        <h2 className="font-heading font-bold text-3xl md:text-4xl text-charcoal tracking-tight">Vérifiez une propriété en 3 secondes</h2>
        <p className="mt-3 text-sm md:text-base text-charcoal/70">Pointez la caméra sur n'importe quel QR code Tagora pour valider l'authenticité et le statut de l'objet.</p>
      </div>

      <div className="card-brutal bg-white p-5 md:p-7" data-testid="tg-scanner-card">
        {scanState === 'idle' && (
          <div className="aspect-video rounded-xl border-2 border-dashed border-charcoal/15 bg-surface-alt flex flex-col items-center justify-center gap-4 p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Camera className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm text-charcoal/70 max-w-sm">Aucune donnée n'est transmise. La vérification s'effectue localement, puis interroge l'annuaire Tagora de manière anonyme.</p>
            <button onClick={onStart} className="btn-primary tg-btn-glow inline-flex items-center justify-center gap-2" data-testid="tg-scanner-start">
              <Camera className="w-4 h-4" /> Activer la caméra
            </button>
          </div>
        )}

        {scanState === 'scanning' && (
          <div className="relative">
            <div id="tg-qr-reader" ref={scannerHostRef} className="rounded-xl overflow-hidden" />
            <button onClick={onStop} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-charcoal/80 hover:bg-charcoal text-white flex items-center justify-center transition-colors" aria-label="Arrêter le scan" data-testid="tg-scanner-stop">
              <X className="w-4 h-4" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-charcoal/80 text-white text-xs inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" /> Recherche d'un QR Tagora...
            </div>
          </div>
        )}

        {scanState === 'success' && demoResult && (
          <div className="space-y-5" data-testid="tg-scanner-result">
            <div className="aspect-video rounded-xl bg-secondary/30 border-2 border-charcoal/10 flex flex-col items-center justify-center gap-3">
              <div className="w-14 h-14 rounded-full bg-white border-2 border-charcoal flex items-center justify-center">
                <ShieldCheck className="w-7 h-7 text-primary" strokeWidth={2.5} />
              </div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-charcoal">Propriété vérifiée</p>
            </div>
            <div className="rounded-xl bg-surface-alt border-2 border-charcoal/10 p-5">
              <p className="text-xs text-charcoal/60 uppercase tracking-widest mb-2">Objet certifié</p>
              <p className="font-heading font-bold text-2xl text-charcoal">{demoResult.name}</p>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-charcoal/60 text-xs uppercase tracking-wider mb-1">Tagora ID</p>
                  <p className="font-mono text-[13px] text-charcoal break-all">{demoResult.tagoraId}</p>
                </div>
                <div>
                  <p className="text-charcoal/60 text-xs uppercase tracking-wider mb-1">Certifié le</p>
                  <p className="text-charcoal">{demoResult.certifiedAt}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={onReset} className="btn-outline flex-1 inline-flex items-center justify-center" data-testid="tg-scanner-rescan">Nouveau scan</button>
              <Link to={`/verify/${demoResult.tagoraId}`} className="btn-primary flex-1 inline-flex items-center justify-center" data-testid="tg-scanner-details">
                Voir le certificat complet
              </Link>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-lg border-2 border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger" data-testid="tg-scanner-error">
            {error}
          </div>
        )}
      </div>
    </div>
  </section>
);

// ---------- B2B ----------
const B2B_CARDS = [
  {
    title: 'Fabricants & créateurs',
    desc: "Équipementiers sportifs, lunetiers, joailliers, artistes, marques indépendantes — certifiez votre catalogue à l'échelle, avec votre identité de marque.",
    bullets: ['Génération QR illimitée en moins de 30s', 'Branding personnalisé (logo, couleurs)', 'Tableau de bord temps réel'],
  },
  {
    title: 'Assureurs & experts',
    desc: 'Vérification instantanée de propriété et de provenance. Preuve cryptographique opposable, infrastructure prête pour le DPP européen.',
    bullets: ['Preuve cryptographique opposable', "Réduction de fraude jusqu'à 80%", 'Compatible DPP EU 2026'],
  },
  {
    title: 'Marketplaces & revente',
    desc: 'Traçabilité du cycle de vie complète, transfert certifié entre propriétaires successifs, badge Tagora visible pour vos acheteurs.',
    bullets: ['Historique infalsifiable', 'Transfert en 1 clic', 'Trust signal pour vos acheteurs'],
  },
];

const B2BBlock = () => (
  <section className="relative bg-surface-alt/40 border-y-2 border-charcoal/10" data-testid="tg-b2b-section">
    <div className="max-w-6xl mx-auto px-6 md:px-8 py-20 md:py-24">
      <div className="text-center mb-12">
        <p className="text-xs tracking-[0.25em] uppercase text-charcoal/60 mb-3">03 — Pour les professionnels</p>
        <h2 className="font-heading font-bold text-3xl md:text-4xl text-charcoal tracking-tight">Une API. Vos produits certifiés à l'échelle.</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {B2B_CARDS.map((c, i) => (
          <div key={c.title} className="card-brutal bg-white p-6 flex flex-col" data-testid={`tg-b2b-card-${i}`}>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-primary mb-3">0{i + 1}</p>
            <h3 className="font-heading font-bold text-xl text-charcoal mb-3">{c.title}</h3>
            <p className="text-charcoal/70 text-sm leading-relaxed mb-5">{c.desc}</p>
            <ul className="space-y-2.5 mt-auto">
              {c.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-charcoal">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="text-center mt-12">
        <Link to="/business#contact" className="btn-primary tg-btn-glow inline-flex items-center gap-2" data-testid="tg-b2b-cta">
          Demander une démonstration technique <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);

// ---------- Standard & Crédibilité ----------
const StandardBlock = () => (
  <section className="relative" data-testid="tg-standard-section">
    <div className="max-w-5xl mx-auto px-6 md:px-8 py-20 md:py-24">
      <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-start">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-charcoal/60 mb-3">04 — Standard ouvert</p>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-charcoal leading-tight tracking-tight">Standard ouvert. Aucun lock-in. Aucune dépendance.</h2>
          <p className="mt-5 text-charcoal/70 text-sm md:text-base leading-relaxed">
            Tagora publie son protocole de vérification ainsi que la spécification complète du Tagora&nbsp;ID.
            Tout fabricant peut émettre, vérifier et transférer un certificat sans intermédiaire commercial.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to="/whitepaper" className="btn-outline inline-flex items-center justify-center gap-2" data-testid="tg-link-whitepaper">
              <FileText className="w-4 h-4" /> Whitepaper
            </Link>
            <Link to="/developers" className="btn-outline inline-flex items-center justify-center gap-2" data-testid="tg-link-api">
              <Code2 className="w-4 h-4" /> Documentation API
            </Link>
          </div>
        </div>
        <div className="space-y-3">
          {[
            { label: 'Conformité RGPD', value: 'Données personnelles chiffrées, aucun PII en clair côté serveur.' },
            { label: 'Prêt pour le DPP EU 2026', value: 'Infrastructure de base compatible Digital Product Passport européen (ESPR). Les champs DPP étendus (composition, empreinte CO₂, REACH) seront ajoutés sur demande client.' },
            { label: 'Audit cryptographique', value: 'Checksum Crockford Base32 + mod-37. Vérifiable offline.' },
          ].map((row) => (
            <div key={row.label} className="card-brutal bg-white p-5">
              <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-primary mb-2">{row.label}</p>
              <p className="text-sm text-charcoal leading-relaxed">{row.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ============== Page ==============

const Landing = () => {
  useAuth(); // keep auth context alive for the global Header
  const [scanState, setScanState] = useState('idle'); // idle | scanning | success
  const [error, setError] = useState('');
  const [demoResult, setDemoResult] = useState(null);
  const scannerRef = useRef(null);
  const scannerHostRef = useRef(null);

  const extractTagoraId = (raw) => {
    if (!raw) return null;
    const txt = raw.trim();
    const m = txt.match(/TGR-[0-9A-Z]{4}-[0-9A-Z]{4}-[0-9A-Z]{4}-[0-9A-Z]{4}/i);
    if (m) return m[0].toUpperCase();
    try {
      const u = new URL(txt);
      const seg = u.pathname.split('/').filter(Boolean).pop();
      if (seg && /^TGR-/i.test(seg)) return seg.toUpperCase();
    } catch (_) { /* not a URL */ }
    return null;
  };

  const stopScan = useCallback(async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch (_) { /* ignore */ }
    scannerRef.current = null;
  }, []);

  const handleScanSuccess = useCallback(async (decodedText) => {
    const id = extractTagoraId(decodedText);
    if (!id) return;
    await stopScan();
    setDemoResult({
      tagoraId: id,
      name: 'Objet certifié Tagora',
      certifiedAt: new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }),
    });
    setScanState('success');
  }, [stopScan]);

  const startScan = useCallback(async () => {
    setError('');
    setScanState('scanning');
    setTimeout(async () => {
      try {
        const inst = new Html5Qrcode('tg-qr-reader');
        scannerRef.current = inst;
        await inst.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          handleScanSuccess,
          () => { /* per-frame decode errors are silenced */ },
        );
      } catch (err) {
        setScanState('idle');
        setError(err?.message || 'Caméra indisponible. Vérifiez les autorisations.');
      }
    }, 80);
  }, [handleScanSuccess]);

  const resetScanner = useCallback(() => {
    setDemoResult(null);
    setError('');
    setScanState('idle');
  }, []);

  useEffect(() => () => { stopScan(); }, [stopScan]);

  const scrollToScanner = () => {
    const el = document.getElementById('tg-scanner');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => { if (scanState === 'idle') startScan(); }, 600);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="tg-landing">
      <PageStyles />
      <Header />
      <main>
        <HeroBlock onScanClick={scrollToScanner} />
        <ScannerBlock
          scannerHostRef={scannerHostRef}
          scanState={scanState}
          error={error}
          demoResult={demoResult}
          onStart={startScan}
          onStop={() => { stopScan(); setScanState('idle'); }}
          onReset={resetScanner}
        />
        <B2BBlock />
        <StandardBlock />
      </main>
    </div>
  );
};

export default Landing;
